import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { trackEvent } from "../analytics";
import { AUTH_REDIRECT_STORAGE_KEY } from "../lib/analyticsEvents";
import {
  isKitaLoginIntent,
  loginBackTarget,
  sanitizeAppPath,
  LOGIN_SUBTITLE_DEFAULT,
  LOGIN_SUBTITLE_KITA,
} from "../lib/guestAcquisition";
import { fetchSchoolEmails } from "../api/schoolEmails";
import { useAuth } from "../contexts/AuthContext";

export default function LoginPage() {
  const { user, loading, signInWithPassword, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = sanitizeAppPath(searchParams.get("redirect") || "/");
  const backTarget = loginBackTarget({
    redirectTo,
    back: searchParams.get("back"),
  });
  const isSchuleRedirect =
    redirectTo === "/schule" || redirectTo.startsWith("/schule?");
  const utmScenario = searchParams.get("utm_scenario");
  const kitaIntent = isKitaLoginIntent({ redirectTo, utmScenario });
  const urlError = searchParams.get("error");
  const urlErrorDesc = searchParams.get("error_description");

  useEffect(() => {
    if (urlError) {
      setError(urlErrorDesc || urlError);
    }
  }, [urlError, urlErrorDesc]);

  useEffect(() => {
    if (!loading && user) {
      if (!isSchuleRedirect) {
        navigate(redirectTo, { replace: true });
        return;
      }
      let cancelled = false;
      (async () => {
        try {
          const data = await fetchSchoolEmails();
          if (cancelled) return;
          const hasExtractions = (data.items || []).length > 0;
          if (!hasExtractions) {
            navigate("/?schoolSetup=1", { replace: true });
          } else {
            navigate("/schule", { replace: true });
          }
        } catch {
          if (!cancelled) navigate("/?schoolSetup=1", { replace: true });
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [user, loading, navigate, redirectTo, isSchuleRedirect]);

  const storeAuthRedirect = () => {
    try {
      sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, redirectTo);
    } catch {
      /* ignore */
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    try {
      storeAuthRedirect();
      await signInWithGoogle(redirectTo);
    } catch (err) {
      setError(err.message || "Google-Anmeldung fehlgeschlagen.");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }
    if (!password) {
      setError("Bitte gib dein Passwort ein.");
      return;
    }
    setSubmitting(true);
    try {
      storeAuthRedirect();
      if (isSignUp) {
        await signUp(trimmed, password);
        trackEvent("sign_up", { method: "email", redirect_path: redirectTo });
        setSuccessMessage("Prüfe deine E-Mails, um dein Konto zu bestätigen.");
      } else {
        await signInWithPassword(trimmed, password);
      }
    } catch (err) {
      setError(err.message || "Anmeldung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card">
          <p className="login-loading">Wird geladen…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">Anmelden</h1>
        <p className="login-subtitle">
          {kitaIntent ? LOGIN_SUBTITLE_KITA : LOGIN_SUBTITLE_DEFAULT}
        </p>

        {error && <p className="login-error">{error}</p>}
        {successMessage && <p className="login-success">{successMessage}</p>}

        <>
          <button
            type="button"
            className="add-card-btn add-card-btn-google"
            onClick={handleGoogleSignIn}
          >
            <svg className="login-google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Mit Google anmelden
          </button>
          <p className="login-divider">oder</p>
          <form onSubmit={handleEmailSubmit} className="login-email-form">
            <label className="add-card-label">
              E-Mail
              <input
                type="email"
                className="add-card-input"
                placeholder="deine@email.ch"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                autoComplete="email"
              />
            </label>
            <label className="add-card-label">
              Passwort
              <input
                type="password"
                className="add-card-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                autoComplete={isSignUp ? "new-password" : "current-password"}
              />
            </label>
            <button
              type="submit"
              className="add-card-btn add-card-btn-primary"
              disabled={submitting}
            >
              {submitting
                ? "Bitte warten…"
                : isSignUp
                  ? "Konto erstellen"
                  : "Anmelden"}
            </button>
            <p className="login-toggle">
              {isSignUp ? (
                <>
                  Bereits ein Konto?{" "}
                  <button
                    type="button"
                    className="login-toggle-btn"
                    onClick={() => {
                      setIsSignUp(false);
                      setSuccessMessage("");
                      setError("");
                    }}
                  >
                    Anmelden
                  </button>
                </>
              ) : (
                <>
                  Noch kein Konto?{" "}
                  <button
                    type="button"
                    className="login-toggle-btn"
                    onClick={() => {
                      setIsSignUp(true);
                      setError("");
                    }}
                  >
                    Registrieren
                  </button>
                </>
              )}
            </p>
          </form>
        </>

        <p className="login-back">
          <Link to={backTarget}>Zurück</Link>
        </p>
      </div>
    </div>
  );
}
